/*
 * Release Description block (v2 — metafield-only, Eastern Time)
 *
 * The Liquid template renders BOTH the pre- and post-release panes (whichever
 * metafields have content) and makes a best-guess about which is visible. This
 * element is the SOURCE OF TRUTH: it interprets the release wall-clock as
 * America/New_York (DST-correct, no hard-coded offset) and reconciles
 * visibility against the visitor's clock, so CDN-cached HTML self-corrects.
 *
 * Data attributes consumed:
 *   data-has-release          "true" | "false"
 *   data-release-wallclock    "YYYY-MM-DDTHH:MM:SS" meant in America/New_York
 *
 * connectedCallback re-runs whenever the section is re-rendered in the theme
 * editor (shopify:section:load replaces the DOM), so editor re-renders are
 * handled without an extra listener.
 */

const RELEASE_TZ = 'America/New_York';

/*
 * Convert a wall-clock string meant in America/New_York into the true UTC
 * epoch (ms), handling EDT/EST automatically. Two-pass technique:
 *   1. Read the wall-clock as if it were UTC.
 *   2. Ask Intl what the ET clock reads at that instant; the difference is the
 *      zone offset, which we subtract to land on the real UTC instant.
 */
function etWallclockToEpoch(wallclock) {
  const match = wallclock.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  if (!match) return NaN;

  const [, y, mo, d, h, mi, s] = match.map(Number);
  const asUTC = Date.UTC(y, mo - 1, d, h, mi, s);

  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: RELEASE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const parts = {};
  for (const part of dtf.formatToParts(new Date(asUTC))) {
    parts[part.type] = part.value;
  }

  const etReadingAsUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  // offset = how far ET is from UTC at this instant (negative in the Americas).
  const offset = etReadingAsUTC - asUTC;
  return asUTC - offset;
}

class ReleaseDescription extends HTMLElement {
  #timeoutId = 0;
  #releaseMs = NaN;

  connectedCallback() {
    // No release date: the server-side decision (post-release) is final.
    if (this.getAttribute('data-has-release') !== 'true') return;

    const wallclock = this.getAttribute('data-release-wallclock') || '';
    this.#releaseMs = etWallclockToEpoch(wallclock);
    if (!Number.isFinite(this.#releaseMs)) return;

    this.#reconcile();
    this.#scheduleLiveSwitch();
  }

  disconnectedCallback() {
    this.#clearTimer();
  }

  #reconcile() {
    this.#show(Date.now() >= this.#releaseMs ? 'post' : 'pre');
  }

  #show(which) {
    const pre = this.querySelector('[data-pane="pre"]');
    const post = this.querySelector('[data-pane="post"]');
    if (pre) pre.toggleAttribute('hidden', which !== 'pre');
    if (post) post.toggleAttribute('hidden', which !== 'post');
  }

  // Flip to post-release at the exact moment, no reload — only when the release
  // is in the future and within ~24h. One timer, no polling.
  #scheduleLiveSwitch() {
    const delay = this.#releaseMs - Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;
    if (delay <= 0 || delay > DAY_MS) return;

    this.#clearTimer();
    this.#timeoutId = window.setTimeout(() => {
      this.#show('post');
      this.#timeoutId = 0;
    }, delay);
  }

  #clearTimer() {
    if (this.#timeoutId) {
      window.clearTimeout(this.#timeoutId);
      this.#timeoutId = 0;
    }
  }
}

if (!customElements.get('release-description')) {
  customElements.define('release-description', ReleaseDescription);
}
